import { Fragment, useSyncExternalStore, type MouseEvent, type ReactNode } from 'react';
import {
  inlineParagraph,
  parseInline,
  parseMarkdownBlocks,
  quoteParagraphs,
  type InlineToken,
  type MarkdownBlock,
  type MarkdownList,
  type ReferenceTarget,
} from '@/domain/markdown';
import type { Translator } from '@/i18n';
import { useDirectory } from '@/lib/directory-context';
import { resolveActor } from '@/lib/directory';
import { useT } from '@/lib/locale-context';
import { childHref } from '@/lib/trail';
import { CheckSquare, Square } from './icons';
import { Avatar } from './ui/avatar';
import { Chip } from './ui/chip';
import { ItemGlyph } from './values/item-value';
import styles from './markdown.module.css';

/**
 * The kit's markdown renderer for prose (docs/FIELDS_AND_TAGS.md §2: text/prose
 * renders as a document section): the description, the note, a module's prose
 * fields and the feed's quoted text. Deliberately a SUBSET, sized to what the
 * ledger and a phone write (2026-10-24):
 *
 * - blocks: headings `#` to `######` (1 and 2 render h4, 3 renders h5, 4 to 6
 *   render h6, so a prose field never outranks the page's own heads),
 *   paragraphs, nested lists (`-` `*` `+` and `1.` `1)`, mixed by level; an
 *   ordered list keeps its first number; task items `- [ ]` / `- [x]` show a
 *   read-only box), blockquotes, fenced code, pipe tables (alignment markers
 *   accepted and ignored), thematic breaks;
 * - inline: code spans first (verbatim), then bold, italic (`_` only at word
 *   boundaries, so `snake_case_name` stays literal), `~~strike~~`, links, bare
 *   http(s) autolinks, references, backslash escapes.
 *
 * **A newline inside a paragraph is a line break**: text typed on a phone
 * ("Milch⏎Eier⏎Brot") renders as the lines it was typed in, and a blank line
 * starts a new paragraph. Everything outside the subset renders as text.
 *
 * **A reference renders as a chip** (docs/PROSE.md §5.3). A person's chip
 * carries their avatar and their current name from the viewer's directory;
 * where the directory does not name them, the stored label shows as muted
 * text with no chip, so the page never shows a raw id or a name the viewer
 * may not see. An item's chip carries the stored label and links to the item
 * (`linkItem`, the host's trail; else a plain `/items/<id>`). Drawing a
 * reference reads nothing: no gather, no second gate.
 *
 * **Safe by construction**: the parser emits React elements, never HTML
 * strings, so there is no sanitization surface (no dangerouslySetInnerHTML).
 * A link is an http(s) URL or a path on this origin (`/items/…`); anything
 * else (`javascript:`, `data:`) stays literal text. An image `![alt](url)`
 * renders as a link with its alt text and is never fetched: a remote image in
 * shared prose would let its author see every reader who opens the page.
 *
 * **Never throws, linear in the input**: one pass per line and one pass per
 * inline run; code spans, link brackets and emphasis are matched through
 * precomputed indexes and per-character opener stacks, never by searching
 * forward from every candidate.
 *
 * Hand-built over a markdown dep for the reason the UI kit is: the subset is
 * small, the output wears our tokens, and a full CommonMark engine is surface
 * we do not want. The parser is pure and lives in `src/domain/markdown.ts`.
 * The component reads the locale, the viewer's directory and the page's
 * origin, so it renders inside client components.
 */

// ---------------------------------------------------------------- render ---

const noSubscription = () => () => {};

/** The page's origin; null while the server renders and the client hydrates
 *  (both then treat an absolute link as another site's). */
function usePageOrigin(): string | null {
  return useSyncExternalStore(
    noSubscription,
    () => window.location.origin,
    () => null,
  );
}

function originOf(href: string): string | null {
  try {
    return new URL(href).origin;
  } catch {
    return null;
  }
}

interface RenderContext {
  origin: string | null;
  t: Translator;
  linkItem: (id: string) => string;
}

/** Whether a link is on this origin (a `/…` path, or an http(s) URL of the
 *  page's own origin): it then navigates in the same tab, with no trail, since
 *  a typed link is its author's, not a drilldown. Any other link opens a new
 *  tab. */
export function linksInPlace(href: string, origin: string | null): boolean {
  return href.startsWith('/') || (origin !== null && originOf(href) === origin);
}

function renderLink(href: string, children: ReactNode, ctx: RenderContext, key: number) {
  return linksInPlace(href, ctx.origin) ? (
    <a key={key} href={href}>
      {children}
    </a>
  ) : (
    <a key={key} href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  );
}

function renderInline(tokens: readonly InlineToken[], ctx: RenderContext): ReactNode[] {
  return tokens.map((token, i) => {
    switch (token.kind) {
      case 'text':
        return <Fragment key={i}>{token.text}</Fragment>;
      case 'break':
        return <br key={i} />;
      case 'code':
        return <code key={i}>{token.text}</code>;
      case 'bold':
        return <strong key={i}>{renderInline(token.children, ctx)}</strong>;
      case 'italic':
        return <em key={i}>{renderInline(token.children, ctx)}</em>;
      case 'strike':
        return <del key={i}>{renderInline(token.children, ctx)}</del>;
      case 'link':
        return renderLink(token.href, renderInline(token.children, ctx), ctx, i);
      case 'reference':
        return (
          <ReferenceChip
            key={i}
            target={token}
            label={token.label}
            href={token.ref === 'item' ? ctx.linkItem(token.id) : undefined}
          />
        );
    }
  });
}

/**
 * A reference as a chip (docs/PROSE.md §5.3), at rest and in the prose
 * editor alike. A person's chip: their avatar and the name the viewer's
 * directory gives them now; a person the directory does not name is the label
 * the author wrote, muted, with no chip and no link. An item's chip: the label
 * the author picked and a neutral glyph (its health is a read this chip does
 * not make), a link to the item where `href` is given. Reads nothing.
 */
export function ReferenceChip({
  target,
  label,
  href,
  title,
}: {
  target: ReferenceTarget;
  label: string;
  /** An item chip's link (the host's trail); none in the editor. */
  href?: string;
  /** A quiet note on the chip (the editor's "cannot see this item"). */
  title?: string;
}) {
  const directory = useDirectory();
  if (target.ref === 'person') {
    const ref = `person:${target.id}`;
    if (!directory[ref]) {
      return (
        <span
          className={styles.referenceUnnamed}
          title={title}
          data-testid="reference-person-unnamed"
        >
          {label}
        </span>
      );
    }
    const actor = resolveActor(ref, directory);
    return (
      <span className={styles.reference}>
        <Chip
          lead={{ avatar: <Avatar actor={actor} size={16} title={false} /> }}
          title={title}
          testId="reference-person"
          data-ref={ref}
        >
          {actor.name}
        </Chip>
      </span>
    );
  }
  const chip = { lead: { icon: <ItemGlyph /> }, testId: 'reference-item', title };
  return (
    <span className={styles.reference}>
      {href !== undefined ? (
        <Chip {...chip} href={href} data-ref={`item:${target.id}`}>
          {label}
        </Chip>
      ) : (
        <Chip {...chip} data-ref={`item:${target.id}`}>
          {label}
        </Chip>
      )}
    </span>
  );
}

const inline = (text: string, ctx: RenderContext) => renderInline(parseInline(text), ctx);

function renderQuote(lines: readonly string[], ctx: RenderContext, key: number) {
  return (
    <blockquote key={key}>
      {quoteParagraphs(lines).map((p, i) => (
        <p key={i}>{inline(p, ctx)}</p>
      ))}
    </blockquote>
  );
}

/** A task item's box: read-only, since a checklist is the checklist rail. */
function TaskBox({ checked, t }: { checked: boolean; t: Translator }) {
  return (
    <span
      className={styles.box}
      role="img"
      aria-label={t(checked ? 'fields.markdown.taskChecked' : 'fields.markdown.taskOpen')}
    >
      {checked ? <CheckSquare aria-hidden weight="fill" /> : <Square aria-hidden />}
    </span>
  );
}

function renderList(list: MarkdownList, ctx: RenderContext, key: number): ReactNode {
  const items = list.items.map((item, j) => (
    <li key={j} className={item.task === null ? undefined : styles.task}>
      {item.task !== null && <TaskBox checked={item.task} t={ctx.t} />}
      {item.content.map((c, k) => {
        if (c.kind === 'list') return renderList(c, ctx, k);
        if (c.kind === 'quote') return renderQuote(c.lines, ctx, k);
        // The item's first text sits on the marker's line; a later one (after
        // a blank line or a child list) is a paragraph of its own.
        return k === 0 ? (
          <Fragment key={k}>{inline(c.text, ctx)}</Fragment>
        ) : (
          <p key={k}>{inline(c.text, ctx)}</p>
        );
      })}
    </li>
  ));
  return list.ordered ? (
    <ol key={key} start={list.start === 1 ? undefined : list.start}>
      {items}
    </ol>
  ) : (
    <ul key={key}>{items}</ul>
  );
}

function renderBlock(b: MarkdownBlock, ctx: RenderContext, i: number): ReactNode {
  switch (b.kind) {
    case 'heading': {
      const H = b.level <= 2 ? 'h4' : b.level === 3 ? 'h5' : 'h6';
      return <H key={i}>{inline(b.text, ctx)}</H>;
    }
    case 'paragraph':
      return <p key={i}>{inline(b.text, ctx)}</p>;
    case 'quote':
      return renderQuote(b.lines, ctx, i);
    case 'code':
      return (
        <pre key={i}>
          <code>{b.lines.join('\n')}</code>
        </pre>
      );
    case 'list':
      return renderList(b, ctx, i);
    case 'table':
      // The wrapper scrolls sideways on a narrow screen; the page never does.
      return (
        <div key={i} className={styles.table} data-testid="markdown-table">
          <table>
            <thead>
              <tr>
                {b.header.map((cell, k) => (
                  <th key={k}>{inline(cell, ctx)}</th>
                ))}
              </tr>
            </thead>
            {b.rows.length > 0 && (
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, k) => (
                      <td key={k}>{inline(cell, ctx)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      );
    case 'rule':
      return <hr key={i} />;
  }
}

/** An item reference's link where the host gives none: the item's page,
 *  with no trail. */
const plainItemHref = (id: string) => childHref(id, undefined, []);

/**
 * Rendered markdown prose. Its type is sized in `em` from its own font size,
 * so a host sets one size and the headings and code follow. `inline` renders
 * the inline marks and line breaks only, as one paragraph (the note: a short
 * notice has no block structure).
 */
export function Markdown({
  text,
  className,
  inline: inlineOnly = false,
  linkItem = plainItemHref,
}: {
  text: string;
  className?: string;
  inline?: boolean;
  /** Where an item reference links: the host's drilldown with its trail
   *  (`src/lib/trail.ts`). Without one, the item's plain page. */
  linkItem?: (id: string) => string;
}) {
  const t = useT();
  const origin = usePageOrigin();
  const ctx: RenderContext = { origin, t, linkItem };
  return (
    <div className={className ? `${styles.prose} ${className}` : styles.prose}>
      {inlineOnly ? (
        <p>{inline(inlineParagraph(text), ctx)}</p>
      ) : (
        parseMarkdownBlocks(text).map((b, i) => renderBlock(b, ctx, i))
      )}
    </div>
  );
}

/**
 * Whether a click inside a rest box (rendered prose that opens its editor on
 * a click) opens the editor: not when it landed on a link, which the browser
 * follows instead, and not when it ended a text selection inside the box.
 */
export function clickOpensEditor(e: MouseEvent<HTMLElement>): boolean {
  if (e.target instanceof Element && e.target.closest('a')) return false;
  const selection = window.getSelection();
  if (
    selection &&
    !selection.isCollapsed &&
    selection.anchorNode &&
    e.currentTarget.contains(selection.anchorNode)
  ) {
    return false;
  }
  return true;
}

/**
 * Where a click that opens an editor landed inside the rest box, relative to
 * the box's top-left corner (the prose editor's `caretAt`, so the caret lands
 * there), or undefined when it landed outside the box.
 */
export function caretOf(
  e: MouseEvent,
  box: HTMLElement | null,
): { x: number; y: number } | undefined {
  if (!box || !(e.target instanceof Node) || !box.contains(e.target)) return undefined;
  const r = box.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}
